/**
 * Tournament list and entry point to create/manage tournaments.
 */
import { useEffect, useState } from "react";
import type { Tournament } from "@sportsos/engine";
import { api } from "../api.ts";

interface Props {
  readonly onCreateNew: () => void;
  readonly onSelect: (id: string) => void;
}

export function Tournaments({ onCreateNew, onSelect }: Props): React.ReactElement {
  const [tournaments, setTournaments] = useState<readonly Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const data = await api.listTournaments();
        if (live) setTournaments(data.tournaments);
      } catch {
        // Silent — empty list is fine
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  return (
    <div className="app-page">
      <header className="ds-appbar">
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">Tournaments</b>
        </span>
      </header>

      <button
        className="ds-btn ds-btn--primary ds-btn--block"
        onClick={onCreateNew}
        style={{ marginBottom: "var(--ds-space-5)" }}
      >
        Create Tournament
      </button>

      {loading ? <div className="ds-caption">Loading...</div> : null}

      {!loading && tournaments.length === 0 ? (
        <div className="ds-card ds-card--flat" style={{ padding: "var(--ds-space-8) var(--ds-space-4)", textAlign: "center" }}>
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            style={{ width: 40, height: 40, margin: "0 auto var(--ds-space-3)", display: "block", fill: "none", stroke: "var(--ds-text-muted)", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" }}
          >
            <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
            <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11" />
            <path d="M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
            <path d="M12 14v3" />
            <path d="M8.5 20h7" />
          </svg>
          <div style={{ fontWeight: 700, fontSize: "var(--ds-text-md)", marginBottom: "var(--ds-space-1)" }}>
            No tournaments yet
          </div>
          <div className="ds-caption">Create one to manage fixtures, standings, and leaderboards.</div>
        </div>
      ) : null}

      <div className="ds-list">
        {tournaments.map((tournament) => (
          <button
            key={tournament.id}
            className="ds-list-item"
            onClick={() => onSelect(tournament.id)}
          >
            <span className="ds-iconslot ds-iconslot--amber" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11" />
                <path d="M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
                <path d="M12 14v3" />
                <path d="M8.5 20h7" />
              </svg>
            </span>
            <span className="ds-list-item__body">
              <b className="ds-list-item__title">{tournament.name}</b>
              <span className="ds-list-item__meta">
                {tournament.startDate} — {tournament.endDate} · {tournament.format === "round_robin" ? "Round Robin" : "Groups + Knockout"}
              </span>
              <span className="ds-list-item__meta">{tournament.ruleSet.name}</span>
            </span>
            <span className="ds-badge ds-badge--accent" style={{ flex: "none" }}>
              {tournament.public ? "Public" : "Private"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
